import { OrderStatus } from '../enums/order-status.enum';
import type { ValidationIssue } from '../models/types/order-preferences.types';

/**
 * PD-16276 — how the CRM order paths report a refused placement, and which
 * status changes count as placing an order.
 */

/**
 * The CRM submit error string: every blocking issue, in its self-contained
 * form, joined into one sentence. API callers and the cart's submit toast have
 * always received this shape, so it stays a plain string.
 */
export function formatOrderSubmissionFailure(
	issues: ReadonlyArray<Pick<ValidationIssue, 'message' | 'detail'>>,
): string {
	return `Order submission failed: ${issues
		.map((issue) => issue.detail ?? issue.message)
		.join('; ')}`;
}

/** Statuses an order sits in before it is placed. */
const PRE_PLACEMENT_STATUSES: ReadonlyArray<string> = [
	OrderStatus.UNSUBMITTED,
	OrderStatus.QUOTE,
	OrderStatus.QUOTE_NEEDS_REVIEW,
];

/**
 * Statuses that do not place an order even when a cart or quote moves into
 * them: back into a cart or quote, or straight to a cancellation or
 * rejection.
 */
const NON_PLACEMENT_TARGET_STATUSES: ReadonlyArray<string> = [
	...PRE_PLACEMENT_STATUSES,
	OrderStatus.CANCELLED,
	OrderStatus.OVERRIDE_REJECTED,
];

/**
 * True when a status change places a cart or quote as an order. Any status
 * outside the non-placement list counts, including ERP-supplied custom
 * statuses, so a direct status edit cannot skip the order checks that
 * submitting would run.
 */
export function isOrderPlacementStatusChange(
	fromStatus: string | null | undefined,
	toStatus: string | null | undefined,
): boolean {
	if (fromStatus == null || toStatus == null) {
		return false;
	}

	return (
		PRE_PLACEMENT_STATUSES.includes(fromStatus) &&
		!NON_PLACEMENT_TARGET_STATUSES.includes(toStatus)
	);
}
